import { createContext, useContext, useState, useEffect, useRef } from "react";
import { loginRequest, validateTokenRequest, refreshRequest, logoutRequest } from "@/api/auth.service";
import { tokenRef, authCallbacks } from "@/api/api";
import type { ReactNode } from 'react';
import { parseUserRole, type User } from "@/api/user.service";
import type { Client } from "@/api/client.service";

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<User>;
  validateToken: () => Promise<void>;
  logout: () => void;
  setLoggedClient: (client: Client) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

class AuthenticationError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'AuthenticationError';

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthenticationError);
    }
  }
}

function getTokenExp(token: string): number | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(atob(part));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRestoredSession = useRef(false);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleProactiveRefresh = (token: string) => {
    clearRefreshTimer();
    const exp = getTokenExp(token);
    if (!exp) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const ttlSec = exp - nowSec;
    // Refresh at 80% of remaining TTL (never less than 10s from now)
    const delayMs = Math.max((ttlSec * 0.8) * 1000, 10_000);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const data = await refreshRequest();
        tokenRef.current = data.token;
        scheduleProactiveRefresh(data.token);
      } catch {
        doLogout();
      }
    }, delayMs);
  };

  const doLogout = () => {
    clearRefreshTimer();
    tokenRef.current = null;
    setUser(null);
    localStorage.removeItem("user");
  };

  // Wire callbacks so apiFetch can call refresh/logout without importing AuthContext
  useEffect(() => {
    authCallbacks.refresh = async () => {
      const data = await refreshRequest();
      tokenRef.current = data.token;
      scheduleProactiveRefresh(data.token);
      return data.token;
    };
    authCallbacks.logout = doLogout;

    return () => {
      authCallbacks.refresh = null;
      authCallbacks.logout = null;
    };
  }, []);

  useEffect(() => {
    // Guards against React StrictMode's dev-only double-invoke of mount
    // effects: without this, two concurrent /refresh calls would fire
    // against the same (single-use, rotated) cookie — one wins, the other
    // 401s and the server clears the cookie, wiping out the very session
    // the winning call just restored.
    if (hasRestoredSession.current) return;
    hasRestoredSession.current = true;

    const run = async () => {
      const savedUser = localStorage.getItem("user");

      if (savedUser) {
        try {
          // Try a silent refresh to restore the session from the httpOnly cookie.
          const data = await refreshRequest();
          tokenRef.current = data.token;
          scheduleProactiveRefresh(data.token);
          setUser(JSON.parse(savedUser));
        } catch {
          // Cookie expired or missing — clear stale user data.
          localStorage.removeItem("user");
        }
      }

      setLoading(false);
    };

    run();

    return () => clearRefreshTimer();
  }, []);

  const login = async (email: string, password: string) => {
    const data = await loginRequest(email, password);

    const newUser: User = {
      id: data.user_id,
      email,
      name: data.name,
      role: parseUserRole(data.role),
      active: data.active,
      phone: data.phone
    };

    tokenRef.current = data.token;
    scheduleProactiveRefresh(data.token);
    setUser(newUser);

    // Store only user profile — token lives in memory, refresh token in httpOnly cookie.
    localStorage.setItem("user", JSON.stringify(newUser));

    return newUser;
  };

  const validateToken = async () => {
    if (!tokenRef.current) throw new AuthenticationError("Usuário não autenticado");
    await validateTokenRequest(tokenRef.current);
  };

  const logout = () => {
    logoutRequest().catch(() => { });
    doLogout();
  };

  const setLoggedClient = (client: Client) => {
    if (!user) return;
    const newUser = { ...user, loggedClient: client };
    setUser(newUser);
    localStorage.setItem("user", JSON.stringify(newUser));
  };

  return (
    <AuthContext.Provider value={{ user, login, validateToken, logout, setLoggedClient, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
